import styles from '../Navigation.module.scss'
import NavigationItem from '../navigation-item/NavigationItem.tsx'
import { routes } from 'types/routes.ts'
import { clearUser } from 'utils/app.ts'
import { useTranslation } from 'react-i18next'
import { memo, useEffect, useMemo, useRef } from 'react'
import { NavigationProps } from '../types.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { Button, Icon, LoadingSkeleton, Tag } from '@boltenergy-be/design-system'
import { UserTypes } from 'store/auth/types.ts'
import { useLocation, useNavigate } from 'react-router'
import LanguageSwitchBar from 'components/language-switch-bar/LanguageSwitchBar.tsx'
import { checkHasBudgetMeter, isTerminatedContractPastEndDate } from 'utils/contracts.ts'
import useToggleWithClickOutside from 'hooks/useToggleWithClickOutside.ts'
import Link from 'components/link/Link.tsx'
import classNames from 'classnames'
import { selectCurrentAccount, selectIsProducerAccount } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { EVENT_CATEGORIES } from 'constants/tracking.ts'
import { IS_PROD_TESTING } from 'constants/envs.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { getActiveSocialTariffAttestation } from 'store/contact/utils.ts'
import { useGetCustomerPaymentPlanQuery } from 'store/queries/bolt-backend/customers'

const DesktopAppNavigation = memo(({ handleLogout, withAction }: NavigationProps) => {
  // Redux
  const { selected, accounts, billingContracts, contact, myContact } = useStoreSelector((store) => store.contact)
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const { userType } = useStoreSelector((store) => store.auth)
  const account = useStoreSelector(selectCurrentAccount)

  // Redux queries
  const { currentData } = useGetCustomerPaymentPlanQuery(
    { customerNumber: account?.customerNumber ?? '' },
    { skip: !account?.customerNumber || userType !== UserTypes.SUPER_USER }
  )

  // Not using the Redux selector because this is also used for Sales Portal
  const electricity =
    userType !== UserTypes.SALES && selected.billingContract && billingContracts
      ? billingContracts[selected.billingContract]?.serviceContracts?.electricity
      : undefined

  // Access rights
  const billingCyclesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)
  const marketPricesAccessRights = useGetAccessRight(ACCESS_RIGHT_KEYS.marketPrices)

  // i18n
  const { t } = useTranslation()

  // React router
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { isOpen, setIsOpen } = useToggleWithClickOutside(dropdownRef)

  // Constants
  const terminatedContractPastEndDate = isTerminatedContractPastEndDate(electricity)
  const hasBudgetMeter = checkHasBudgetMeter(electricity)
  const activeAttestation = useMemo(
    () => (userType === UserTypes.SUPER_USER ? getActiveSocialTariffAttestation(account?.attestations, undefined) : undefined),
    [userType, account?.attestations]
  )

  /**
   * Closes dropdown on route change
   */
  useEffect(() => {
    if (pathname) setIsOpen(false)
  }, [pathname])

  return (
    <aside className={classNames(styles['navigation-wrapper'], { [styles.superuser]: userType === UserTypes.SUPER_USER })}>
      <LanguageSwitchBar compact sticky />

      <nav className={styles.navigation} aria-labelledby="main">
        {userType === UserTypes.SUPER_USER && (
          <ul className={styles['superuser-actions']}>
            <li>
              <Button
                leadingIcon="shuffle"
                variant="layered"
                onClick={() => {
                  clearUser()
                  void navigate(routes.CUSTOMER_EMAIL)
                }}
              >
                Customer
              </Button>
            </li>
            <li>
              <Link representation="button" leadingIcon="menu" variant="layered" href={routes.EVENT_LOG}>
                Events
              </Link>
            </li>
          </ul>
        )}

        {accounts?.[selected.account] && (
          <div ref={dropdownRef} className={styles.dropdown}>
            <button
              aria-expanded={isOpen}
              className={styles['account-info-card']}
              onClick={() => {
                setIsOpen(!isOpen)
              }}
            >
              {myContact.loading ? (
                <LoadingSkeleton.Rectangle width="100%" height={15} className={classNames(styles.name, 'mb-200')} />
              ) : (
                <strong className={classNames('truncate', styles.name)}>{accounts[selected.account].name}</strong>
              )}

              {myContact.loading ? (
                <LoadingSkeleton.Rectangle width="100%" height={15} className={styles.address} />
              ) : (
                <small className={classNames('text-subdued', styles.address)}>
                  {selected.account}
                  {accounts[selected.account].billingContracts.length > 1 &&
                    ` — ${accounts[selected.account].billingContracts.length} ${t(($) => $.nav.addressCard.addresses)}`}
                </small>
              )}
              <Icon className={styles.icon} name="chevronDown" />

              {userType === UserTypes.SUPER_USER && electricity && (
                <div className={styles['super-user-tags']}>
                  {!!contact && (
                    <Tag size="small" type="neutral">
                      {contact?.dbId ? 'Account created' : 'Account not created'}
                    </Tag>
                  )}

                  {!!activeAttestation && (
                    <Tag size="small" type="informative">
                      {t(($) => $.superUserTags.socialTariff)}
                    </Tag>
                  )}

                  {!!currentData?.hasActivePaymentPlan && (
                    <Tag size="small" type="soft">
                      {t(($) => $.superUserTags.paymentPlan)}
                    </Tag>
                  )}
                </div>
              )}
            </button>

            <ul style={{ display: isOpen ? 'flex' : 'none' }} className={styles.list} aria-hidden={!isOpen}>
              {accounts && Object.keys(accounts).length > 1 && (
                <NavigationItem
                  id="switch-account"
                  icon="shuffle"
                  text={t(($) => $.nav.switchAccount)}
                  onClick={() => {
                    void navigate(routes.ACCOUNT_SELECTION)
                  }}
                />
              )}
              <NavigationItem id="logout" icon="logOut" text={t(($) => $.nav.logout)} onClick={handleLogout} />
            </ul>
          </div>
        )}

        <ul className={styles['nav-items-list']}>
          <NavigationItem icon="homeSmile" text={t(($) => $.nav.overview)} url={routes.OVERVIEW} />
          <NavigationItem
            icon="charts"
            text={t(($) => $.nav[isProducer ? 'injection' : 'usage'])}
            extraActiveRoutes={[routes.CONSUMPTION]}
            url={routes.CONSUMPTION_ELECTRICITY}
          />
          <NavigationItem
            icon="file"
            text={t(($) => $.nav.billing)}
            overrideInactive={[routes.BILLING_CYCLES]}
            extraActiveRoutes={[routes.BILLING]}
            url={routes.BILLING_INVOICES}
          />
          {!terminatedContractPastEndDate && billingCyclesAccessRights.canAccess.current && !hasBudgetMeter && (
            <NavigationItem
              icon="thumbsUp"
              text={t(($) => $.nav.instalmentCheck)}
              url={routes.BILLING_INSTALMENT_CHECK}
              onClick={() =>
                trackEvent(
                  { category: EVENT_CATEGORIES.INSTALMENT_CHECK, object: 'navigation', action: 'click' },
                  { navigationType: 'desktop' }
                )
              }
            />
          )}
          {marketPricesAccessRights.todayTomorrow.canAccess && (
            <NavigationItem icon="euro" text={t(($) => $.nav.marketPrices)} url={routes.MARKET_PRICES} />
          )}
          {!isProducer && <NavigationItem icon="home" text={t(($) => $.nav.move)} extraActiveRoutes={[routes.MOVE]} url={routes.MOVE} />}
          {!isProducer && (
            <NavigationItem
              icon="peopleHeart"
              text={t(($) => $.nav.friends)}
              extraActiveRoutes={[routes.REFERRAL]}
              url={routes.REFERRAL_INFO}
              tag={
                withAction
                  ? {
                      text: t(($) => $.action),
                      type: 'positive'
                    }
                  : undefined
              }
            />
          )}
          <NavigationItem
            icon="electricity"
            text={t(($) => $.nav[isProducer ? 'myProducerProfile' : 'myProducer'])}
            url={routes.PRODUCER}
          />
          {isProducer && <NavigationItem icon="badgeCheck" text={t(($) => $.nav.guaranteeOfOrigin)} url={routes.GUARANTEE_OF_ORIGIN} />}
          {!isProducer && <NavigationItem icon="leaf" text={t(($) => $.nav.energyOverview)} url={routes.ENERGY_OVERVIEW} />}
          <NavigationItem icon="contract" text={t(($) => $.nav.myAddresses)} url={routes.CONTRACT} />
          <NavigationItem icon="userCircle" text={t(($) => $.nav.user)} extraActiveRoutes={[routes.USER]} url={routes.USER_PERSONAL} />
          <NavigationItem
            icon="speechQuestion"
            text={t(($) => $.nav.support)}
            extraActiveRoutes={[routes.SUPPORT]}
            url={routes.SUPPORT_CONTACT}
          />
        </ul>

        {!IS_PROD_TESTING && userType === UserTypes.SUPER_USER && (
          <>
            <small className="text-subdued mt-500">SUPERUSER ONLY</small>
            <ul>
              <NavigationItem icon="contract" text={t(($) => $.nav.tariffs)} url={routes.TARIFFS_OVERVIEW} />
            </ul>
          </>
        )}
      </nav>
    </aside>
  )
})

DesktopAppNavigation.displayName = 'DesktopAppNavigation'

export default DesktopAppNavigation
